import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const transitionWrapperTypes = {
  basic: 'basic',
  fade: 'fade',
  itemSimple: 'itemSimple',
  slide: 'slide',
  scale: 'scale',
  rotate: 'rotate',
  scaleAndFade: 'scaleAndFade',
  simpleFade: 'simpleFade',
  whiteToBlack: 'whiteToBlack',
  default: 'default', // Add more transition types as needed
  none: 'none'
}

const TransitionWrapper = ({ children,
  transitionWrapperType = transitionWrapperTypes.fade,
  useStyle = true,
  useMulti = false,
  className
}) => {

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => ref.current && observer.unobserve(ref.current);
  }, [children]);

  const transition = useMemo(() => {
    const _defaults = {
      duration: 0.5,
      scale: 0.9,
      opacity: 0,
    };

    switch (transitionWrapperType) {
      case transitionWrapperTypes.basic:
        return {
          initial: { opacity: .9 },
          animate: { opacity: 1 },
          transition: { duration: 0.1 },
        };
      case transitionWrapperTypes.itemSimple:
        return {
          initial: { opacity: .9, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { x: '2%' },
          transition: { duration: 0.1 },
        };
      case transitionWrapperTypes.slide:
        return {
          initial: { x: '2%' },
          animate: { x: 0 },
          exit: { x: '2%' },
          transition: { duration: 0.5 },
        };
      case transitionWrapperTypes.scale:
        return {
          initial: { scale: _defaults.scale },
          animate: { scale: 1 },
          exit: { scale: 0 },
          transition: { duration: 0.5 },
        };
      case transitionWrapperTypes.rotate:
        return {
          initial: { rotate: -180 },
          animate: { rotate: 0 },
          exit: { rotate: 180 },
          transition: { duration: 0.5 },
        };
      case transitionWrapperTypes.scaleAndFade:
        return {
          initial: { scale: _defaults.scale, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0, opacity: 0 },
          transition: { duration: 0.5 },
        };
      case transitionWrapperTypes.whiteToBlack:
        return {
          initial: { backgroundColor: 'white' },
          animate: { backgroundColor: 'black' },
          exit: { backgroundColor: 'white' },
          transition: { duration: 1 },
        };
      case transitionWrapperTypes.simpleFade:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.2 },
        };
      default:
        return {
          initial: { opacity: _defaults.opacity },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.5 },
        };
    }
  }, [transitionWrapperType]);

  // Render children wrapped with motion.div individually if useMulti is true
  if (useMulti) {
    return (
      <>
        {React.Children.map(children, (child, index) => (
          <motion.div
            ref={ref}
            key={index}
            initial={transition.initial}
            animate={isVisible ? transition.animate : transition.initial}
            exit={transition.exit}
            transition={transition.transition}
            className={className}
          >
            {child}
          </motion.div>
        ))}
      </>
    );
  }

  // console.log('transitionWrapperType', transitionWrapperType, children)

  switch (transitionWrapperType) {
    case transitionWrapperTypes.none:
      return React.Children.map(children, (child, index) => (
        <div key={index} className={className} ref={ref}>
          {child}
        </div>
      ))
    default:
      return <motion.div
        ref={ref}
        initial={transition.initial} // Pass initial state
        animate={isVisible ? transition.animate : transition.initial}
        exit={transition.exit} // Pass exit state (if necessary)
        transition={transition.transition} // Use the transition duration
        className={'transition-wrapper ova'}
      >
        {children}
      </motion.div >
  }

};

export { transitionWrapperTypes };
export default TransitionWrapper;
